import { useCallback, useState, useRef } from 'react'
import classNames from 'classnames'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form/dist/index.ie11'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faCheck } from '@fortawesome/pro-regular-svg-icons'
import formatError from '../../../utilities/formatError'
import { Stack } from '../../stack'
import { Select } from '../../select'
import { Button } from '../../button'
import { TextField } from '../../textfield'
import { Alert } from '../../alert'
import { Row, Col } from '../../grid'
import { Heading } from '../../typography/Heading'
import computeOffsetTop from '../../../utilities/computeTopOffset'
import useEnabledOnClient from '../../../utilities/useEnabledOnClient'
import * as styles from '../CalculatorModule.module.scss'

const validationSchema = {
  residence: {
    min: 0,
    max: 999999999,
  },
}

const PFCashFlowCalculator = () => {
  const [t, i18n] = useTranslation()
  const [language, countryCode] = i18n.language.split('-')
  const [calcResults, setCalcResults] = useState()
  // Our API doesn’t support sync form submissions and so we need Javascript to submit the form asynchronously by issuing an XHR. Therefore, we need to ensure that the button is initially disabled and gets re-enabled once all Javascript has loaded on the client.
  const disabled = useEnabledOnClient()

  const { register, errors, handleSubmit } = useForm({
    mode: 'onBlur',
    defaultValues: {
      mode: 0,
      wages: 0,
      interest: 0,
      dividends: 0,
      socialsecurity: 0,
      pensions: 0,
      alimony: 0,
      otherincome: 0,
      charity: 0,
      mortgage: 0,
      vacation: 0,
      autoloan: 0,
      persloan: 0,
      cc: 0,
      fedtaxes: 0,
      statetaxes: 0,
      fica: 0,
      retaxes: 0,
      othertaxes: 0,
      utilities: 0,
      repairs: 0,
      food: 0,
      clothing: 0,
      education: 0,
      childcare: 0,
      autoexp: 0,
      transexp: 0,
      lifeins: 0,
      homeowners: 0,
      autoins: 0,
      medical: 0,
      entertain: 0,
      travel: 0,
      club: 0,
      hobbies: 0,
      gifts: 0,
      homeimp: 0,
      services: 0,
      other: 0,
    },
  })

  const [inProgress, setInProgress] = useState(false)
  const [alert, showAlert] = useState(null)
  const dismissAlert = useCallback(() => showAlert(null), [])
  const resultsContainer = useRef(null)
  const resultsHeader = useRef(null)

  const fieldGroups = [
    {
      label: t('PFCashFlowCalculator.itemizedIncomeLabel', {
        defaultValue: 'Itemized income',
      }),
      fields: [
        {
          label: t('PFCashFlowCalculator.wagesSalaryTipsLabel', {
            defaultValue: 'Wages, salary and tips ($)',
          }),
          name: 'wages',
        },
        {
          label: t('PFCashFlowCalculator.interestSavingsAccountsLabel', {
            defaultValue: 'Interest on savings accounts, CDs, etc. ($)',
          }),
          name: 'interest',
        },
        {
          label: t('PFCashFlowCalculator.dividendsStocksLabel', {
            defaultValue: 'Dividends from stocks, etc. ($)',
          }),
          name: 'dividends',
        },
        {
          label: t('PFCashFlowCalculator.socialSecurityBenefitsLabel', {
            defaultValue: 'Social security benefits ($)',
          }),
          name: 'socialsecurity',
        },
        {
          label: t('PFCashFlowCalculator.PensionsLabel', {
            defaultValue: 'Pensions ($)',
          }),
          name: 'pensions',
        },
        {
          label: t('PFCashFlowCalculator.AlimonChildSupportLabel', {
            defaultValue: 'Alimony, child support (received) ($)',
          }),
          name: 'alimony',
        },
        {
          label: t('PFCashFlowCalculator.otherIncomeLabel', {
            defaultValue: 'Other income  ($)',
          }),
          name: 'otherincome',
        },
      ],
    },
    {
      label: t('PFCashFlowCalculator.itemizedExpensesLabel', {
        defaultValue: 'Itemized expenses',
      }),
      fields: [
        {
          label: t('PFCashFlowCalculator.charitableChurchDonationsLabel', {
            defaultValue: 'Charitable/church donations',
          }),
          name: 'charity',
        },
        {
          label: t('PFCashFlowCalculator.mortgagePaymentRentLabel', {
            defaultValue: 'Mortgage payment or rent ($)',
          }),
          name: 'mortgage',
        },
        {
          label: t('PFCashFlowCalculator.vacationHomeMortgageLabel', {
            defaultValue: 'Vacation home (mortgage) ($)',
          }),
          name: 'vacation',
        },
        {
          label: t('PFCashFlowCalculator.automobileLoansLabel', {
            defaultValue: 'Automobile loan(s) ($)',
          }),
          name: 'autoloan',
        },
        {
          label: t('PFCashFlowCalculator.personalLoansLabel', {
            defaultValue: 'Personal loan(s) ($)',
          }),
          name: 'persloan',
        },
        {
          label: t('PFCashFlowCalculator.chargeAccountsLabel', {
            defaultValue: 'Charge accounts ($)',
          }),
          name: 'cc',
        },
        {
          label: t('PFCashFlowCalculator.federalIncomeTaxesLabel', {
            defaultValue: 'Federal income taxes ($)',
          }),
          name: 'fedtaxes',
        },
        {
          label: t('PFCashFlowCalculator.stateIncomeTaxesLabel', {
            defaultValue: 'State income taxes ($)',
          }),
          name: 'statetaxes',
        },
        {
          label: t('PFCashFlowCalculator.FICALabel', {
            defaultValue: 'FICA (social security taxes) ($)',
          }),
          name: 'fica',
        },
        {
          label: t('PFCashFlowCalculator.realEstateTaxesLabel', {
            defaultValue: 'Real estate taxes ($)',
          }),
          name: 'retaxes',
        },
        {
          label: t('PFCashFlowCalculator.OtherTaxesLabel', {
            defaultValue: 'Other taxes ($)',
          }),
          name: 'othertaxes',
        },
        {
          label: t('PFCashFlowCalculator.utilitiesLabel', {
            defaultValue: 'Utilities ($)',
          }),
          name: 'utilities',
        },
        {
          label: t('PFCashFlowCalculator.householdRepairsLabel', {
            defaultValue: 'Household repairs and maintenance ($)',
          }),
          name: 'repairs',
        },
        {
          label: t('PFCashFlowCalculator.foodLabel', {
            defaultValue: 'Food ($)',
          }),
          name: 'food',
        },
        {
          label: t('PFCashFlowCalculator.clothingLaundryLabel', {
            defaultValue: 'Clothing and laundry ($)',
          }),
          name: 'clothing',
        },
        {
          label: t('PFCashFlowCalculator.educationalExpensesLabel', {
            defaultValue: 'Educational expenses ($)',
          }),
          name: 'education',
        },
        {
          label: t('PFCashFlowCalculator.childCareLabel', {
            defaultValue: 'Child care ($)',
          }),
          name: 'childcare',
        },
        {
          label: t('PFCashFlowCalculator.automobileExpensesLabel', {
            defaultValue: 'Automobile expenses (gas, repairs, etc.) ($)',
          }),
          name: 'autoexp',
        },
        {
          label: t('PFCashFlowCalculator.otherTransportationExpensesLabel', {
            defaultValue: 'Other transportation expenses ($)',
          }),
          name: 'transexp',
        },
        {
          label: t('PFCashFlowCalculator.lifeInsuranceLabel', {
            defaultValue: 'Life insurance premiums ($)',
          }),
          name: 'lifeins',
        },
        {
          label: t('PFCashFlowCalculator.homeownersRentersLabel', {
            defaultValue: 'Homeowners (renters) insurance ($)',
          }),
          name: 'homeowners',
        },
        {
          label: t('PFCashFlowCalculator.automobileInsuranceLabel', {
            defaultValue: 'Automobile insurance ($)',
          }),
          name: 'autoins',
        },
        {
          label: t('PFCashFlowCalculator.medicalDentalInsuranceLabel', {
            defaultValue: 'Medical, dental and disability insurance ($)',
          }),
          name: 'medical',
        },
        {
          label: t('PFCashFlowCalculator.entertainmentDiningLabel', {
            defaultValue: 'Entertainment and dining ($)',
          }),
          name: 'entertain',
        },
        {
          label: t('PFCashFlowCalculator.recreationTravelLabel', {
            defaultValue: 'Recreation and travel ($)',
          }),
          name: 'travel',
        },
        {
          label: t('PFCashFlowCalculator.clubDuesLabel', {
            defaultValue: 'Club dues ($)',
          }),
          name: 'club',
        },
        {
          label: t('PFCashFlowCalculator.hobbiesLabel', {
            defaultValue: 'Hobbies ($)',
          }),
          name: 'hobbies',
        },
        {
          label: t('PFCashFlowCalculator.giftsLabel', {
            defaultValue: 'Gifts ($)',
          }),
          name: 'gifts',
        },
        {
          label: t('PFCashFlowCalculator.majorHomeImprovementsLabel', {
            defaultValue: 'Major home improvements and furnishings ($)',
          }),
          name: 'homeimp',
        },
        {
          label: t('PFCashFlowCalculator.professionalServicesLabel', {
            defaultValue: 'Professional services ($)',
          }),
          name: 'services',
        },
        {
          label: t('PFCashFlowCalculator.OtherMiscellaneousExpensesLabel', {
            defaultValue: 'Other and miscellaneous expenses ($)',
          }),
          name: 'other',
        },
      ],
    },
  ]

  const handleSubmitValid = async formData => {
    setInProgress(true)
    showAlert(false)

    try {
      const url = 'https://www.calcxml.com/rest/bud09'

      // Documentation
      // https://www.calcxml.com/documentation/rest/Bud09JSONRequest.htm

      const payload = {
        username: process.env.CALCXML_USERNAME,
        password: process.env.CALCXML_PASSWORD,
        language,
        countryCode,
        chartWidth: 900,
        chartHeight: 400,
        barColor1: '63B5E4',
        barColor2: '84BD00',
        barColor3: 'FCC841',
        // barColor4: '#ffffff',
        // barColor5: '#ffffff',
        // barColor6: '#ffffff',
        mode: formData.mode,
        wages: formData.wages,
        interest: formData.interest,
        dividends: formData.dividends,
        socialsecurity: formData.socialsecurity,
        pensions: formData.pensions,
        alimony: formData.alimony,
        otherincome: formData.otherincome,
        charity: formData.charity,
        mortgage: formData.mortgage,
        vacation: formData.vacation,
        autoloan: formData.autoloan,
        persloan: formData.persloan,
        cc: formData.cc,
        fedtaxes: formData.fedtaxes,
        statetaxes: formData.statetaxes,
        fica: formData.fica,
        retaxes: formData.retaxes,
        othertaxes: formData.othertaxes,
        utilities: formData.utilities,
        repairs: formData.repairs,
        food: formData.food,
        clothing: formData.clothing,
        education: formData.education,
        childcare: formData.childcare,
        autoexp: formData.autoexp,
        transexp: formData.transexp,
        lifeins: formData.lifeins,
        homeowners: formData.homeowners,
        autoins: formData.autoins,
        medical: formData.medical,
        entertain: formData.entertain,
        travel: formData.travel,
        club: formData.club,
        hobbies: formData.hobbies,
        gifts: formData.gifts,
        homeimp: formData.homeimp,
        services: formData.services,
        other: formData.other,
      }

      const response = await axios.post(url, payload)

      if (response.status !== 200 || Boolean(response.data.error)) {
        throw new Error(`Unexpected status code: ${response.status} ${response.statusText}`)
      }

      setInProgress(false)
      setCalcResults(response.data)
      window.scrollTo({
        top: computeOffsetTop(resultsContainer.current) - 20,
        behavior: 'smooth',
      })
      resultsHeader.current.focus()
    } catch (error) {
      setInProgress(false)
      showAlert({
        type: 'error',
        title: t('PFCashFlowCalculator.error', {
          defaultValue: 'Error',
        }),
        message: error.message,
      })
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  const chunkArray = (array, chunkSize) => {
    let index = 0
    const arrayLength = array.length
    const tempArray = []

    for (index = 0; index < arrayLength; index += chunkSize) {
      const chunk = array.slice(index, index + chunkSize)
      tempArray.push(chunk)
    }

    return tempArray
  }

  return (
    <div>
      {alert && (
        <Row>
          <Col xs={12}>
            <Alert variant={alert.type} title={alert.title} onDismiss={dismissAlert}>
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}
      <form
        action="/"
        method="POST"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleSubmitValid)}
        className={alert ? styles.HasAlert : ''}
      >
        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('PFCashFlowCalculator.firstLegend', {
              defaultValue: 'Assumptions',
            })}
          </legend>
          <Row>
            <Col xs={12} lg={6}>
              <Select
                label={t('PFCashFlowCalculator.monthlyAnnualFiguresLabel', {
                  defaultValue: 'Monthly or annual figures?',
                })}
                id="mode"
                name="mode"
                ref={register(validationSchema.mode)}
                error={formatError(errors.mode, validationSchema.mode, t)}
                autoFocus
              >
                <Select.Option value="0">
                  {t('PFCashFlowCalculator.monthlyAnnualFiguresOptions.monthly', {
                    defaultValue: 'Monthly',
                  })}
                </Select.Option>
                <Select.Option value="1">
                  {t('PFCashFlowCalculator.monthlyAnnualFiguresOptions.annual', {
                    defaultValue: 'Annual',
                  })}
                </Select.Option>
              </Select>
            </Col>
          </Row>
        </fieldset>
        {fieldGroups.map(group => (
          <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
            <legend className={styles.Legend}>{group.label}</legend>
            {chunkArray(group.fields, 2).map(item => (
              <Row>
                <Col xs={12} lg={6}>
                  <TextField
                    label={item[0].label}
                    id={item[0].name}
                    name={item[0].name}
                    type="number"
                    ref={register(validationSchema[item[0].name])}
                    error={formatError(errors[item[0].name], validationSchema[item[0].name], t)}
                    autoSelect
                  />
                </Col>
                {item[1] && (
                  <Col xs={12} lg={6}>
                    <TextField
                      label={item[1].label}
                      id={item[1].name}
                      name={item[1].name}
                      type="number"
                      ref={register(validationSchema[item[1].name])}
                      error={formatError(errors[item[1].name], validationSchema[item[1].name], t)}
                      autoSelect
                    />
                  </Col>
                )}
              </Row>
            ))}
          </fieldset>
        ))}

        <Stack alignment="center" className={styles.Submit}>
          <Button
            type="submit"
            disabled={inProgress || disabled}
            loading={inProgress}
            alt={t('PFCashFlowCalculator.calculateAlt', {
              defaultValue: 'Calculate to view results',
            })}
          >
            {t('PFCashFlowCalculator.calculate', {
              defaultValue: 'Calculate',
            })}
          </Button>

          {alert && alert.type === 'error' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faTimes} className={styles.TimesIcon} />
              {t('PFCashFlowCalculator.couldNotCalculate', {
                defaultValue: 'Sorry, could not calculate',
              })}
            </span>
          )}

          {alert && alert.type === 'success' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faCheck} className={styles.CheckIcon} />
              {t('PFCashFlowCalculator.calculationComplete', {
                defaultValue: 'Calculation complete. See below for results!',
              })}
            </span>
          )}
        </Stack>
      </form>

      <div ref={resultsContainer} />

      {calcResults && (
        <div className={styles.Results}>
          <Heading
            className={styles.ResultsHeading}
            element="h3"
            size={3}
            tabIndex="0"
            forwardedRef={resultsHeader}
          >
            {t('PFCashFlowCalculator.results', {
              defaultValue: 'Results',
            })}
          </Heading>
          <p>{calcResults.responseText}</p>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('PFCashFlowCalculator.summaryTable', {
              defaultValue: 'Summary table',
            })}
          </Heading>

          <table className={styles.ResultsTable} role="grid">
            <caption>
              {t('PFCashFlowCalculator.summaryTable', {
                defaultValue: 'Summary table',
              })}
            </caption>
            <tbody>
              <tr>
                <th scope="col">
                  {t('PFCashFlowCalculator.category', {
                    defaultValue: 'Category',
                  })}
                </th>
                <th scope="col">
                  {t('PFCashFlowCalculator.monthly', {
                    defaultValue: 'Monthly',
                  })}
                </th>
                <th scope="col">
                  {t('PFCashFlowCalculator.annual', {
                    defaultValue: 'Annual',
                  })}
                </th>
                <th scope="col">
                  {t('PFCashFlowCalculator.percent', {
                    defaultValue: 'Percent',
                  })}
                </th>
              </tr>
              {calcResults.year.map(item => {
                if (item.monthly === null) {
                  return (
                    <tr className={styles.ResultsTableTotalLine}>
                      <td colSpan="4" className={styles.ResultsTableLeft}>
                        {item.category}
                      </td>
                    </tr>
                  )
                }
                return (
                  <tr>
                    <th scope="row">{item.category}</th>
                    <td>{item.monthly}</td>
                    <td>{item.annual}</td>
                    <td>{item.percent}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('PFCashFlowCalculator.graph', {
              defaultValue: 'Graph',
            })}
          </Heading>
          <img
            src={calcResults.chartUrl}
            alt={t('PFCashFlowCalculator.graphOfResults', {
              defaultValue: 'Graph of results',
            })}
          />
        </div>
      )}
    </div>
  )
}

export { PFCashFlowCalculator }
export default PFCashFlowCalculator
